import { useState } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = { business_name: "", website_url: "", town: "", keyword: "", wants_google: true, grid_size: "7" };

export const NewProspectAuditDialog = ({ open, onOpenChange, quota, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const noGoogleLeft = quota && quota.limit !== null && quota.remaining <= 0;

  const submit = async () => {
    if (!f.business_name.trim() || !f.website_url.trim()) return toast.error("Business name and website are required");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/prospect-audits`, { ...f, wants_google: f.wants_google && !noGoogleLeft, grid_size: Number(f.grid_size) });
      onOpenChange(false);
      setF(EMPTY);
      onCreated(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start the report");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-prospect-audit-dialog">
        <DialogHeader>
          <DialogTitle>New Prospect Audit</DialogTitle>
          <DialogDescription>Website Score and SEO/AEO grades are always included and free to run.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Business name</Label><Input value={f.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Joe's Plumbing" data-testid="pa-business" /></div>
          <div><Label>Website</Label><Input value={f.website_url} onChange={(e) => set("website_url", e.target.value)} placeholder="joesplumbing.com" data-testid="pa-website" /></div>
          <div><Label>Town (optional)</Label><Input value={f.town} onChange={(e) => set("town", e.target.value)} placeholder="Wooster, OH" data-testid="pa-town" /></div>
          <div className="rounded-lg border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Google check</p>
                <p className="text-xs text-muted-foreground">
                  {noGoogleLeft ? "Your agency has used this month's Google checks." : "Map grid, Google profile, search demand, AI answers and the top rival."}
                </p>
              </div>
              <Switch checked={f.wants_google && !noGoogleLeft} disabled={noGoogleLeft} onCheckedChange={(v) => set("wants_google", v)} data-testid="pa-google-switch" />
            </div>
            {f.wants_google && !noGoogleLeft && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Search to check (optional)</Label>
                  <Input value={f.keyword} onChange={(e) => set("keyword", e.target.value)} placeholder="e.g. plumber" data-testid="pa-keyword" /></div>
                <div><Label className="text-xs">Map grid size</Label>
                  <Select value={f.grid_size} onValueChange={(v) => set("grid_size", v)}>
                    <SelectTrigger data-testid="pa-grid-size"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="5">5 × 5 (small town)</SelectItem><SelectItem value="7">7 × 7 (standard)</SelectItem><SelectItem value="9">9 × 9 (wide area)</SelectItem></SelectContent>
                  </Select></div>
                <p className="col-span-2 text-[11px] text-muted-foreground">Leave the search blank to use the business&apos;s Google category. Broad categories (Restaurant, Store) need a search typed in.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="pa-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run report"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
